import React, { useState, useEffect, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Save, X, RotateCcw } from 'lucide-react';

// Haptics utility
const isHapticSupported = () => {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

const haptics = {
  light: () => { if (isHapticSupported()) navigator.vibrate(10); },
  medium: () => { if (isHapticSupported()) navigator.vibrate(25); },
  success: () => { if (isHapticSupported()) navigator.vibrate([10, 50, 10]); },
  warning: () => { if (isHapticSupported()) navigator.vibrate([30, 50, 30]); },
  impact: () => { if (isHapticSupported()) navigator.vibrate(40); },
};

// Sortable wrapper for each widget
const SortableWidget = ({ id, children, isEditing }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled: !isEditing // Disable sorting when not in edit mode
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'opacity-50' : ''} ${
        isEditing ? 'ring-2 ring-purple-400 ring-dashed rounded-xl' : ''
      }`}
    >
      {/* Drag Handle - Only visible in edit mode */}
      {isEditing && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-purple-600 text-white rounded-lg cursor-grab active:cursor-grabbing shadow-lg hover:bg-purple-700 transition-colors touch-none"
          title="Drag to reorder"
        >
          <GripVertical size={20} />
        </div>
      )}
      {children}
    </div>
  );
};

const DashboardEditor = ({ children, isEditing = false, setIsEditing }) => {
  const [widgetOrder, setWidgetOrder] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get widget IDs from children - memoized
  const childArray = useMemo(() => React.Children.toArray(children), [children]);
  
  const defaultOrder = useMemo(() => {
    return childArray.map((child) => {
      // Try multiple ways to get ID
      const id = child.props?.id || child.props?.['data-widget-id'] || child.key;
      return id;
    }).filter(Boolean); // Remove any undefined/null
  }, [childArray]);

  // Load saved order from localStorage on mount
  useEffect(() => {
    if (defaultOrder.length === 0) return;
    
    const savedOrder = localStorage.getItem('dashboardWidgetOrder');
    
    if (savedOrder) {
      try {
        const parsed = JSON.parse(savedOrder);
        // Validate that all current widgets exist in saved order
        const validOrder = parsed.filter((id) => defaultOrder.includes(id));
        // Add any new widgets that weren't in saved order
        const newWidgets = defaultOrder.filter((id) => !validOrder.includes(id));
        setWidgetOrder([...validOrder, ...newWidgets]);
      } catch (e) {
        console.error('Error parsing saved widget order:', e);
        setWidgetOrder([...defaultOrder]);
      }
    } else {
      setWidgetOrder([...defaultOrder]);
    }
    setIsInitialized(true);
  }, [defaultOrder]);

  // Update widget order if children change (new widgets added/removed)
  useEffect(() => {
    if (!isInitialized || defaultOrder.length === 0) return;
    
    // Check if we have new widgets that aren't in the current order
    const newWidgets = defaultOrder.filter((id) => !widgetOrder.includes(id));
    // Check if we have widgets in order that no longer exist
    const removedWidgets = widgetOrder.filter((id) => !defaultOrder.includes(id));
    
    if (newWidgets.length > 0 || removedWidgets.length > 0) {
      setWidgetOrder((currentOrder) => {
        // Remove widgets that no longer exist
        const filtered = currentOrder.filter((id) => defaultOrder.includes(id));
        // Add new widgets at the end
        return [...filtered, ...newWidgets];
      });
    }
  }, [defaultOrder, isInitialized, widgetOrder]);

  // Sensors for both mouse/touch and keyboard - only active in edit mode
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = () => {
    haptics.impact();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      haptics.medium();
      setWidgetOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    haptics.success();
    localStorage.setItem('dashboardWidgetOrder', JSON.stringify(widgetOrder));
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleCancel = () => {
    haptics.light();
    // Reload saved order
    const savedOrder = localStorage.getItem('dashboardWidgetOrder');
    if (savedOrder) {
      try {
        setWidgetOrder(JSON.parse(savedOrder));
      } catch (e) {
        setWidgetOrder([...defaultOrder]);
      }
    } else {
      setWidgetOrder([...defaultOrder]);
    }
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleReset = () => {
    haptics.warning();
    setWidgetOrder([...defaultOrder]);
    localStorage.removeItem('dashboardWidgetOrder');
    localStorage.removeItem('navCardOrder'); 
  window.dispatchEvent(new Event('resetNavCards'));
    setHasChanges(true);
  };

  // Sort children based on widgetOrder
  const sortedChildren = useMemo(() => {
    if (!isInitialized || widgetOrder.length === 0) {
      return childArray;
    }
    
    return widgetOrder
      .map((id) => childArray.find((child) => {
        const childId = child.props?.id || child.props?.['data-widget-id'] || child.key;
        return childId === id;
      }))
      .filter(Boolean);
  }, [widgetOrder, childArray, isInitialized]);

  // Debug logging
  // useEffect(() => {
  //   if (isEditing) {
  //     console.log('DashboardEditor Debug:', {
  //       isEditing,
  //       isInitialized,
  //       childCount: childArray.length,
  //       defaultOrder,
  //       widgetOrder,
  //       sortedChildrenCount: sortedChildren.length
  //     });
  //   }
  // }, [isEditing, isInitialized, childArray.length, defaultOrder, widgetOrder, sortedChildren.length]);

  // Don't render until initialized to prevent flash of unsorted content
  if (!isInitialized || widgetOrder.length === 0) {
    return <div className="space-y-6">{children}</div>;
  }

  return (
    <div className="relative">
      {/* Edit Mode Banner */}
      {isEditing && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600 text-white rounded-lg">
                <GripVertical size={20} />
              </div>
              <div>
                <p className="text-purple-800 dark:text-purple-200 font-semibold">
                  🎨 Edit Mode Active
                </p>
                <p className="text-purple-600 dark:text-purple-400 text-sm">
                  Drag the purple handles to reorder widgets
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <Save size={16} />
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Widgets with Drag and Drop */}
      {isEditing ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={widgetOrder}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-6">
              {sortedChildren.map((child) => {
                const id = child.props?.id || child.props?.['data-widget-id'] || child.key;
                return (
                  <SortableWidget key={id} id={id} isEditing={isEditing}>
                    {child}
                  </SortableWidget>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-6">
          {sortedChildren}
        </div>
      )}
    </div>
  );
};

export default DashboardEditor;